import { useEffect, ReactNode, useMemo } from "react";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CookieBanner from "@/components/site/CookieBanner";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

type DocKey = "privacy" | "terms" | "cookies" | "security" | "dpa";

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Content = ({ docKey, extra }: { docKey: DocKey; extra?: ReactNode }) => {
  const { t, lang } = useLang();
  const doc = t[docKey];
  const updated = lang === "nl" ? "5 mei 2026" : "May 5, 2026";

  useEffect(() => {
    document.title = `${doc.title} · Tribeo`;
  }, [doc.title]);

  const toc = useMemo(
    () => doc.sections.map((s) => ({ id: slug(s.h), label: s.h })),
    [doc.sections],
  );

  return (
    <article className="container max-w-7xl py-32 md:py-40 print:py-8">
      <header className="max-w-3xl">
        <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">{doc.title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {doc.updated}: {updated}
        </p>
        <p className="mt-8 text-lg text-muted-foreground leading-relaxed">{doc.intro}</p>
        <div className="mt-6 print:hidden">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            {lang === "nl" ? "Download als PDF" : "Download as PDF"}
          </Button>
        </div>
      </header>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12">
        <aside className="hidden lg:block print:hidden">
          <nav className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-auto pr-4 border-l border-border">
            <p className="px-4 mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {lang === "nl" ? "Inhoudsopgave" : "Contents"}
            </p>
            <ul className="space-y-1 text-sm">
              {toc.map((i) => (
                <li key={i.id}>
                  <a
                    href={`#${i.id}`}
                    className="block px-4 py-1.5 -ml-px border-l-2 border-transparent hover:border-primary hover:text-primary text-foreground/70 transition-colors"
                  >
                    {i.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="min-w-0 max-w-3xl space-y-12">
          {doc.sections.map((s) => (
            <section key={s.h} id={slug(s.h)} className="scroll-mt-28">
              <h2 className="text-2xl font-extrabold leading-tight">{s.h}</h2>
              {s.body && (
                <p className="mt-3 text-base leading-relaxed text-foreground/85">{s.body}</p>
              )}
              {s.bullets && (
                <ul className="mt-4 space-y-2 list-disc pl-5 text-foreground/85">
                  {s.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              )}
              {s.definitions && (
                <dl className="mt-5 rounded-2xl border border-border divide-y divide-border">
                  {s.definitions.map((d) => (
                    <div key={d.term} className="p-4 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2">
                      <dt className="font-bold text-foreground">{d.term}</dt>
                      <dd className="text-foreground/80">{d.def}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {s.tables?.map((tbl, ti) => (
                <div key={ti} className="mt-5 overflow-x-auto rounded-2xl border border-border">
                  {tbl.caption && (
                    <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-secondary text-foreground/80">
                      {tbl.caption}
                    </div>
                  )}
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/60">
                      <tr>
                        {tbl.headers.map((h) => (
                          <th key={h} className="text-left font-bold px-4 py-2 text-foreground">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tbl.rows.map((row, ri) => (
                        <tr key={ri} className="border-t border-border">
                          {row.map((cell, ci) => (
                            <td key={ci} className="px-4 py-2 text-foreground/85 align-top">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {s.subsections && (
                <div className="mt-5 space-y-5">
                  {s.subsections.map((sub) => (
                    <div key={sub.h}>
                      <h3 className="text-lg font-bold">{sub.h}</h3>
                      {sub.body && (
                        <p className="mt-2 text-base leading-relaxed text-foreground/85">{sub.body}</p>
                      )}
                      {sub.bullets && (
                        <ul className="mt-2 space-y-2 list-disc pl-5 text-foreground/85">
                          {sub.bullets.map((b) => (
                            <li key={b}>{b}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          {extra}

          {doc.contact && (
            <section className="rounded-2xl bg-secondary p-6 text-sm space-y-2">
              {doc.contact.map((c) => (
                <div key={c.label} className="grid grid-cols-[160px_1fr] gap-2">
                  <div className="font-bold">{c.label}</div>
                  <div className="text-foreground/80">{c.value}</div>
                </div>
              ))}
            </section>
          )}

          {doc.history && (
            <section>
              <h2 className="text-xl font-extrabold">
                {lang === "nl" ? "Versiehistorie" : "Version history"}
              </h2>
              <ul className="mt-3 text-sm space-y-1 text-foreground/80">
                {doc.history.map((h) => (
                  <li key={h.version}>
                    <span className="font-bold">{h.version}</span> — {h.date}: {h.note}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </article>
  );
};

export const LegalPage = ({ docKey }: { docKey: DocKey }) => (
  <LanguageProvider>
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <Content docKey={docKey} />
      </main>
      <Footer />
      <CookieBanner />
    </div>
  </LanguageProvider>
);

export default LegalPage;
